<template>
	<div class="comment-box d-flex flex-column justify-content-start" :class="getBoxClass">
		<div class="d-flex flex-column ga-4">
			<div v-if="!isMobile || !isReplyBoxOpen" class="d-flex align-center justify-space-between">
				<!-- always show on DT -->
				<v-card-title class="pa-0 d-flex align-baseline">
					<div class="d-flex media-header">
						<v-icon>icon-chat-stroke</v-icon>
						<span class="heading" v-test="'comments-header'">Comments ({{ showCount }})</span>
					</div>
				</v-card-title>
				<v-btn v-if="isMobile" icon @click="closeCommentBox">
					<v-icon>icon-close-large</v-icon>
				</v-btn>
			</div>
			<AddComment
				v-if="canAddComment"
				ref="addCommentRef"
				:project-id="projectId"
				:project-share-users="projectShareUsers"
				:class="''"
				:lightBoxView="lightBoxView"
				:users="users"
				@update:addComment="addComment" />
		</div>
		<div class="comments-container" ref="commentsContainer" :class="getContainerClass">
			<NoCommentView
				:is-view-only="isViewOnly"
				:is-unauthenticated="!me?.user"
				:has-free-trial-ended="hasFreeTrialEnded"
				:light-box-view="lightBoxView"
				v-if="(comments.length === 0 && !loadingComments) || (isViewOnly && !hasFreeTrialEnded) || !me" />
			<CommentsList
				v-else-if="comments.length > 0"
				:comments="comments"
				:is-view-only="isViewOnly"
				:project-share-users="projectShareUsers"
				:allow-more="allowMore"
				:light-box-view="lightBoxView"
				:users="users"
				:me="me"
				@update-comment="updateComment"
				@reduce-comment-count="reduceCommentCount"
				@edit-comment="editComment"
				@load-more-comments="loadMoreComments"
				@delete-comment="deleteComment"
				@toggle-reply-box="toggleReplyBox" />
			<div class="d-flex justify-center" :class="{ 'loading-indicator-container': lightBoxView }" v-else>
				<v-progress-circular indeterminate color="primary" class="loading-indicator"></v-progress-circular>
			</div>
		</div>
	</div>
</template>

<script>
	import AddComment from "./AddComment.vue";
	import CommentsList from "./CommentsList.vue";
	import NoCommentView from "./NoCommentView.vue";
	import api from "@/api";
	import EventBus from "@/helpers/eventBus";
	import { AccessType } from "@/enums/AccessType";
	import { getMessageFromError, calculateTimeout } from "@/helpers/utilities";

	export default {
		components: {
			AddComment,
			CommentsList,
			NoCommentView,
		},
		props: {
			parentId: {
				type: String,
				required: true,
			},
			parentType: {
				type: String,
				required: true,
			},
			lightBoxView: {
				type: Boolean,
				default: false,
			},
			isViewOnly: {
				type: Boolean,
				default: false,
			},
			conversationId: {
				type: String,
				default: null,
			},
			projectId: {
				type: String,
				default: null,
			},
			users: {
				type: Array,
				default: () => [],
				required: true,
			},
			me: {
				type: Object,
				required: false,
			},
			hasFreeTrialEnded: {
				type: Boolean,
				required: false,
			},
		},
		data() {
			return {
				comments: [],
				commentCount: 0,
				loadingComments: true,
				isReplyBoxOpen: false,
				isEditing: false,
				currentPages: 1,
				currentCount: 0,
				showCount: 0,
				fullHeight: 0,
			};
		},
		async mounted() {
			try {
				this.projectShares = await api.getProjectShares({ where: { projectId: this.projectId } });
				this.fullHeight = window.innerHeight;
			} catch (error) {
				console.error("Error getting project shares", error);
			}
			try {
				await this.loadComments(); // Ensure comments are loaded after setting up the scroll
			} catch (error) {
				console.error("Error loading comments", error);
			}
		},
		methods: {
			closeCommentBox() {
				this.$emit("close-comment-box");
				if (this.isReplyBoxOpen) {
					EventBus.$emit("CloseReplies");
				}
			},
			reduceCommentCount() {
				this.showCount--;
			},
			async loadMoreComments() {
				this.currentPages++;
				await this.loadComments();
			},
			async loadComments(comment) {
				if ((this.isViewOnly && !this.hasFreeTrialEnded) || !this.me) {
					this.loadingComments = false;
					return;
				}
				this.loadingComments = true;
				if (comment) {
					this.comments = [comment, ...this.comments];
					this.currentCount++;
					this.commentCount++;
					this.showCount++;
					this.loadingComments = false;
					return;
				}

				let comments = [],
					commentsToShow = [];
				try {
					[comments, commentsToShow] = await Promise.all([
						api.getComments({
							where: { parentId: this.parentId },
							pageSize: 50 * this.currentPages,
						}),
						api.getComments({
							where: {
								parentId: this.parentId,
								$or: [{ deletedAt: { $eq: null } }, { replyCount: { $gt: 0 } }],
							},
							pageSize: 1,
						}),
					]);
				} catch (error) {
					try {
						[comments, commentsToShow] = await Promise.all([
							api.getCommentsInsecure({
								where: { parentId: this.parentId },
								pageSize: 50 * this.currentPages,
							}),
							api.getCommentsInsecure({
								where: {
									parentId: this.parentId,
									$or: [{ deletedAt: { $eq: null } }, { replyCount: { $gt: 0 } }],
								},
								pageSize: 1,
							}),
						]);
					} catch (error) {
						console.error("Error loading comments insecure: ", error);
					}
				}
				this.currentCount = comments.rows.length;
				this.comments = comments.rows.filter((c) => c.deletedAt === null || c.replyCount > 0);
				this.commentCount = comments.count;
				this.showCount = commentsToShow.count;
				this.loadingComments = false;
			},
			async updateComment(comment) {
				this.comments = this.comments.map((c) => (c.id === comment.id ? comment : c));
			},
			async editComment(comment, isEditing) {
				this.isEditing = isEditing;
			},
			toggleReplyBox(isOpen) {
				this.isReplyBoxOpen = isOpen;
				this.$emit("toggle-reply-box", isOpen);
			},
			async deleteComment() {
				await this.loadComments();
			},
			async addComment(comment) {
				try {
					const body = {
						parentId: this.parentId,
						parentType: this.parentType,
						content: comment,
					};
					if (this.conversationId) {
						body.conversationId = this.conversationId;
					}
					const commentData = await api.createComment(body);
					await this.loadComments(commentData);
				} catch (error) {
					const message = getMessageFromError(error);
					this.$toast.error(message, { timeout: calculateTimeout(message) });
				}
			},
		},
		watch: {
			parentId: async function () {
				await this.loadComments();
				this.$refs.addCommentRef?.clearComment();
				this.isReplyBoxOpen = false;
				this.isEditing = false;
			},
		},
		computed: {
			isMobile() {
				return this.$vuetify.breakpoint.smAndDown;
			},
			projectShareUsers() {
				return this.projectShares?.rows
					?.filter((share) => share.accessType !== AccessType.Viewer && share.User)
					.map((share) => share.User);
			},
			canAddComment() {
				return !this.isReplyBoxOpen && !this.isViewOnly;
			},
			allowMore() {
				return this.commentCount > this.currentCount;
			},
			getBoxClass() {
				return {
					"light-box-comments-box": this.lightBoxView,
				};
			},
			getContainerClass() {
				return {
					"comments-container-no-scroll": this.isReplyBoxOpen && (this.isMobile || this.lightBoxView),
					"light-box-comments-container": this.lightBoxView || this.isMobile,
				};
			},
		},
	};
</script>

<style scoped>
	.media-header {
		align-items: center;
		margin-bottom: 0px;
		color: #03011d;
	}

	.media-header .v-icon {
		width: 28px;
		height: 28px;
		color: #0070ff;
		margin-right: 8px;
		padding: 6px;
	}

	.loading-indicator {
		width: 32px;
		height: 32px;
	}

	.loading-indicator-container {
		position: absolute;
		top: calc(50% - 32px);
		left: calc(50% - 32px);
	}

	.light-box-comments-box {
		height: 100%;
	}

	.comments-container-no-scroll {
		overflow: hidden !important;
	}

	.light-box-comments-container {
		overflow-y: auto !important;
		scrollbar-width: thin;
		min-height: 0;
		flex: 1;
	}

	@media (max-width: 959px) {
		.comment-box {
			padding: 16px 8px !important;
		}
	}
</style>
